// Marketing FAQ content, shared between the About page accordion (client) and
// the homepage's FAQPage JSON-LD (server) so the two never drift apart.

export interface FAQ {
  id: string;
  question: string;
  answer: string;
}

export const faqs: FAQ[] = [
  {
    id: '1',
    question: 'How is Factiii different from other social platforms?',
    answer:
      'Unlike traditional social media, Factiii focuses on truth and verification through community-driven fact-checking. Users work together to verify information and build a reliable knowledge base. Our platform offers unique features including map-based post filtering for location-specific content, versatile factiiis that support multiple data formats (text, images, videos, documents), AI-powered bots for enhanced interactions, mood tracking for personal insights, comprehensive engagement tracking, and privacy-first architecture with optional local-first features that keep your data on your device. We never sell your data and give you complete control over your information.'
  },
  {
    id: '2',
    question: 'How does the fact-checking and verification work?',
    answer:
      'Fact-checking on Factiii is entirely community-driven. Users collaborate to verify information by providing sources, evidence, and peer review. While we encourage users to always verify information independently, our goal is to provide tools and community support for better information literacy and critical thinking.'
  },
  {
    id: '3',
    question: 'What are spaces and how do they work?',
    answer:
      'Spaces are topic-based communities where users can share and discuss verified information. You can join public spaces or create private ones for focused discussions on specific topics.'
  },
  {
    id: '4',
    question: 'What are trons and why would I need them?',
    answer:
      "Trons are Factiii's currency. They power AI interactions, bot conversations, and let you send awards to other users—each use spends trons. The platform is completely free to use without them; trons only unlock AI and other extras."
  },
  {
    id: '5',
    question: 'Is Factiii free to use?',
    answer:
      'Yes! Factiii is completely free to use. All core features including fact-checking, community verification, and basic spaces are available at no cost. AI features run on trons, and each use spends them. Premium is optional: higher AI limits, exclusive spaces, and trons spent at exactly our cost, where everyone else spends a small margin more.'
  },
  {
    id: '6',
    question: 'How do you put humanity over monetization?',
    answer:
      'We refuse engagement hacks that harm mental health: no infinite dopamine traps, no surprise push-notification spam, and no “gotcha” dark patterns. Our default is humane by design—breaks are okay, mindful use is encouraged, and we will never penalize you for stepping away.'
  },
  {
    id: '7',
    question: 'Who owns my data and how is privacy handled?',
    answer:
      'You do. By default, your data stays on your device with optional local-first features. You choose what to share, when to sync, and with whom. We never sell your data, period.'
  },
  {
    id: '8',
    question: 'What do you mean by a fully transparent world?',
    answer:
      'Transparency means clear provenance: posts can carry citations, timestamps, and locations, and we explain why you see content or ads. Our goal is to make it easy to trace information to source and assess accuracy quickly.'
  }
];

// FAQPage JSON-LD for the logged-out homepage. Google retired FAQ rich
// results (May 2026) so this earns no SERP feature — it exists for AI/LLM
// answer engines, which cite Q&A markup far more reliably than accordion UI.
export const generateFaqStructuredData = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});
